import { User } from '@models'
import { UserRoles } from '@modules/common/models/enums/UserRoles'
import { assortmentStringFragments } from '@modules/retail/assortment/graphql'
import { ADD_PRODUCT_TO_RM_ASSORTMENT } from '@modules/retail/assortment/graphql/addProductToRmAssortment'
import { BULK_ADD_PRODUCTS_TO_ASSORTMENT } from '@modules/retail/assortment/graphql/bulkAddProductsToAssortment'
import { BULK_ADD_PRODUCTS_TO_STORE_ASSORTMENT } from '@modules/retail/assortment/graphql/bulkAddProductsToStoreAssortment'
import { BULK_ADD_PRODUCTS_TO_ZONE_ASSORTMENT } from '@modules/retail/assortment/graphql/bulkAddProductsToZoneAssortment'
import { CLUSTER_ASSORTMENT_PRODUCTS } from '@modules/retail/assortment/graphql/clusterAssortmentProducts'
import { CLUSTER_ASSORTMENT_PRODUCTS_HIERARCHY } from '@modules/retail/assortment/graphql/clusterAssortmentProductsHierarchy'
import { GET_BET_BUY_VALUES_FOR_ACTIVITY } from '@modules/retail/assortment/graphql/getActivityBetBuyValues'
import { GET_BET_BUY_VALUES_FOR_STORE } from '@modules/retail/assortment/graphql/getBetBuyValuesForStore'
import { GET_BET_BUY_VALUES_FOR_ZONE } from '@modules/retail/assortment/graphql/getBetBuyValuesForZone'
import { REMOVE_RM_CLUSTER_FROM_PRODUCT } from '@modules/retail/assortment/graphql/removeClusterFromRMAssortment'
import { REMOVE_PRODUCT_FROM_ASSORTMENT } from '@modules/retail/assortment/graphql/removeProductFromAssortment'
import { REMOVE_PRODUCT_FROM_STORE_ASSORTMENT } from '@modules/retail/assortment/graphql/removeProductFromStoreAssortment'
import { REMOVE_PRODUCT_FROM_ZONE_ASSORTMENT } from '@modules/retail/assortment/graphql/removeProductFromZoneAssortment'
import { STORE_ASSORTMENT_PRODUCTS } from '@modules/retail/assortment/graphql/storeAssortmentProducts'
import { STORE_ASSORTMENT_PRODUCTS_HIERARCHY } from '@modules/retail/assortment/graphql/storeAssortmentProductsHierarchyKPIs'
import { ZONE_ASSORTMENT_PRODUCTS } from '@modules/retail/assortment/graphql/zoneAssortmentProducts'
import { ZONE_ASSORTMENT_PRODUCTS_HIERARCHY } from '@modules/retail/assortment/graphql/zoneAssortmentProductsHierarchyKPIs'
import { BUYING_SESSION_PRODUCT } from '@modules/retail/collection/graphql/buyingSessionProduct'
import { BUYING_SESSION_PRODUCTS_HIERARCHY_KPIS } from '@modules/retail/collection/graphql/buyingSessionProductsHierarchyKPIs'
import { GET_COLLECTION_BET_BUY_VALUES } from '@modules/retail/collection/graphql/getCollectionBetBuyValues'
import { UPDATE_PRODUCT_BET_QUANTITY } from '@modules/retail/collection/graphql/updateBuyingSessionProductBetQauntity'
import { UPDATE_PRODUCT_BUY_QUANTITY } from '@modules/retail/collection/graphql/updateBuyingSessionProductBuyQauntity'
import { UPDATE_STORE_PRODUCT_BET_QUANTITY } from '@modules/retail/collection/graphql/updateStoreAssortmentProductBetQauntity'
import { UPDATE_STORE_PRODUCT_BUY_QUANTITY } from '@modules/retail/collection/graphql/updateStoreAssortmentProductBuyQauntity'
import { UPDATE_ZONE_PRODUCT_BET_QUANTITY } from '@modules/retail/collection/graphql/updateZoneAssortmentProductBetQauntity'
import { UPDATE_ZONE_PRODUCT_BUY_QUANTITY } from '@modules/retail/collection/graphql/updateZoneAssortmentProductBuyQauntity'
import { ZONE_COUNTER_OFFER } from '@modules/retail/collection/graphql/zoneCounterOffer'
import { GET_RM_CLUSTERS } from '@modules/retail/navigations/graphql'
import { convertToCurrencyLocaleString } from '@services/assortmentService'
import { storage } from '@services/storageService'
import { apolloClient, nav, stores, strings } from '@stores'
import filter from 'lodash/filter'
import intersectionBy from 'lodash/intersectionBy'
import some from 'lodash/some'
import { action, computed, observable } from 'mobx'

export class AssortmentStore {
  @observable drawerOpen = false
  @observable productList = []
  @observable selectedAssortmentId = null
  @observable rmZoneAssortmentDetails = []
  @observable isOpenDeleteProductConfirmationOnLastRMClusterDeleteDialog = false
  @observable rmZoneAssortmentProductDeleteInProgress = false

  @action
  closeDeleteProductConfirmationOnLastRMClusterDeleteDialog = () => {
    Object.assign(this, {
      isOpenDeleteProductConfirmationOnLastRMClusterDeleteDialog: false
    })
  }

  @action
  openDeleteProductConfirmationOnLastRMClusterDeleteDialog = () => {
    Object.assign(this, {
      isOpenDeleteProductConfirmationOnLastRMClusterDeleteDialog: true
    })
  }

  @action
  dropCluster = (id, type, product, assortmentProductId) => {
    const {
      site: { isGlobalMerchandiserOrAdmin },
      sidebar: { isRegionalMerchandiser },
      product: { handleTagDrop: cmHandleTagDrop },
      assortment: { handleTagDrop },
      nav: {
        params: { assortmentType }
      }
    } = stores

    if (isRegionalMerchandiser()) {
      handleTagDrop(assortmentProductId, type, id)
    } else {
      if (isGlobalMerchandiserOrAdmin() && assortmentType !== strings.cluster) {
        cmHandleTagDrop(product, type, id)
      }
    }
  }

  @computed get selectedAssortmentTab() {
    let result = parseInt(nav.queryParams.tab || '0')

    // GBB-1281 - when switching to a cluster from a zone we need to no longer be on the split table tab
    const { selectedClusterId } = this
    if (selectedClusterId && result === 3) {
      result = 0
    }

    return result
  }

  @observable selectedProductId = null
  @observable selectedClusterId = null
  @observable selectedStoreProductAssortmentId = null
  @observable quantity = null
  @observable isAddStoreClicked = false
  @observable totalQuantityCount = 0
  @observable zoneAssortmentProductWhereInput = null
  @observable bulkAdditionAPIFlag = false
  @observable selectedAssortmentSelectionTab = 'collection'

  /**
   * For deleting / removing product from assortment
   */
  @observable openDeleteConfirmationPopUp = false
  @observable selectedClusterIdForDeletion = null
  @observable selectedProductIdForDeletion = null
  @observable selectedProductForDeletion = null

  assortmentTypeMapping = {
    [strings.cluster]: {
      bulkAddProductMutation: BULK_ADD_PRODUCTS_TO_ASSORTMENT,
      removeProductMutation: REMOVE_PRODUCT_FROM_ASSORTMENT,
      assortmentQuery: CLUSTER_ASSORTMENT_PRODUCTS,
      hierarchicalKPIsQuery: CLUSTER_ASSORTMENT_PRODUCTS_HIERARCHY
    },
    [strings.zone]: {
      bulkAddProductMutation: BULK_ADD_PRODUCTS_TO_ZONE_ASSORTMENT,
      removeProductMutation: REMOVE_PRODUCT_FROM_ZONE_ASSORTMENT,
      assortmentQuery: ZONE_ASSORTMENT_PRODUCTS,
      updateBetQuantityMutation: UPDATE_ZONE_PRODUCT_BET_QUANTITY,
      updateBuyQuantityMutation: UPDATE_ZONE_PRODUCT_BUY_QUANTITY,
      hierarchicalKPIsQuery: ZONE_ASSORTMENT_PRODUCTS_HIERARCHY,
      bulkAddProductRMMutation: ADD_PRODUCT_TO_RM_ASSORTMENT,
      betBuyValuesQuery: GET_BET_BUY_VALUES_FOR_ZONE
    },
    [strings.store]: {
      bulkAddProductMutation: BULK_ADD_PRODUCTS_TO_STORE_ASSORTMENT,
      removeProductMutation: REMOVE_PRODUCT_FROM_STORE_ASSORTMENT,
      assortmentQuery: STORE_ASSORTMENT_PRODUCTS,
      updateBetQuantityMutation: UPDATE_STORE_PRODUCT_BET_QUANTITY,
      updateBuyQuantityMutation: UPDATE_STORE_PRODUCT_BUY_QUANTITY,
      hierarchicalKPIsQuery: STORE_ASSORTMENT_PRODUCTS_HIERARCHY,
      betBuyValuesQuery: GET_BET_BUY_VALUES_FOR_STORE
    },
    [strings.global]: {
      updateBetQuantityMutation: UPDATE_PRODUCT_BET_QUANTITY,
      updateBuyQuantityMutation: UPDATE_PRODUCT_BUY_QUANTITY,
      hierarchicalKPIsQuery: BUYING_SESSION_PRODUCTS_HIERARCHY_KPIS,
      betBuyValuesQuery: GET_COLLECTION_BET_BUY_VALUES
    },
    [strings.activity]: {
      betBuyValuesQuery: GET_BET_BUY_VALUES_FOR_ACTIVITY
    }
  }

  @action
  changeLoadingStatus = status => {
    Object.assign(this, { hasMore: status })
  }

  //Todo need to improve logic.
  @action
  clearSelectedClusterAssortment = () => {
    Object.assign(this, { selectedClusterAssortment: '' })
  }

  @action
  handleChange = (event, productId) => {
    const { productList } = this
    event.target.checked
      ? productList.push(productId)
      : productList.splice(
          productList.findIndex(({ id }) => id === productId),
          1
        )
    Object.assign(this, { productList })
  }

  @action
  updateExportError = errorMsg => {
    Object.assign(this, { exportError: errorMsg })
  }

  getRetailPrice = retailPrice => {
    return retailPrice && retailPrice.priceList && retailPrice.priceList.currency
      ? convertToCurrencyLocaleString(
          retailPrice.priceList.currency.code,
          retailPrice.price
        )
      : 'NA'
  }

  @action handleTagDrop = async (zoneAssortmentId, type, id) => {
    const user = storage.getItem<User>('user')
    const isGlobalMerchandiser = some(user.roles, {
      name: UserRoles.CENTRAL_MERCHANDISER_RETAIL
    })
    if (!isGlobalMerchandiser) {
      await apolloClient.mutate({
        mutation: ADD_PRODUCT_TO_RM_ASSORTMENT,
        variables: {
          assortmentId: id,
          productIds: [zoneAssortmentId]
        }
      })
    }
  }

  @action deleteAssortmentProductFromListView = (e, selectedProduct) => {
    this.openDeleteConfirmationPopUp = true
    this.selectedProductIdForDeletion = selectedProduct.assortmentProductId
    this.selectedProductForDeletion = selectedProduct.buyingSessionProduct
  }

  /**
   * Remove product to cluster assortment
   */
  @action removeAssortmentProduct = async (
    assortmentId,
    assortmentProductId,
    buyingSessionProductId
  ) => {
    const {
      nav: {
        params: { assortmentType, buyingSession: buyingSessionId, podium: podiumId }
      },
      product: {
        getHierarchyMetaFromBuyingSessionProduct,
        buildRefetchQueriesForAttributeHierarchy,
        executeAssortmentRefetchQueries,
        pushKPIRefetchQueryForBetBuyTabsOfCollection
      }
    } = stores

    // Read buying session product fragment from cache
    const buyingSessionProduct = apolloClient.readFragment({
      id: buyingSessionProductId,
      fragment: assortmentStringFragments.buyingSessionProductWithAttributes
    })

    // build refetch query for assortment view on the basis of categories of product and assortment Type
    let refetchQueries = []
    let object = getHierarchyMetaFromBuyingSessionProduct(buyingSessionProduct)
    if (object && assortmentType) {
      refetchQueries = buildRefetchQueriesForAttributeHierarchy([object], assortmentType)
    }

    if (buyingSessionId && podiumId) {
      // Refetch ZONE_COUNTER_OFFER only if on Activity Collection page
      refetchQueries.push({
        query: ZONE_COUNTER_OFFER,
        variables: { buyingSessionId }
      })
    }

    refetchQueries = pushKPIRefetchQueryForBetBuyTabsOfCollection(refetchQueries)

    // Delete Mutation
    await apolloClient
      .mutate({
        mutation: REMOVE_PRODUCT_FROM_ASSORTMENT,
        variables: {
          assortmentId: assortmentId,
          productIds: [assortmentProductId]
        },
        refetchQueries: [
          {
            query: BUYING_SESSION_PRODUCT,
            variables: {
              id: buyingSessionProductId,
              includeUserFavourite: false,
              zoneIdWhere: null
            }
          },
          ...refetchQueries
        ]
      })
      .then(() => {
        // Refresh counts
        executeAssortmentRefetchQueries()
      })
  }

  @action
  setProductDetailsForRMLastClusterDelete = (
    clusterId,
    zoneAssortmentProductId,
    buyingSessionProduct
  ) => {
    Object.assign(this, {
      selectedClusterIdForDeletion: clusterId,
      selectedProductIdForDeletion: zoneAssortmentProductId,
      selectedProductForDeletion: buyingSessionProduct
    })
  }
  @action removeBuyerClusterFromZoneAssortmentProduct = async (
    clusterId,
    zoneAssortmentProductId,
    buyingSessionProduct,
    existingBuyerClusters = []
  ) => {
    let refetchQueries = []

    const associatedBuyerClustersOnCurrentZAP =
      await this.getAssociatedBuyerClustersOnCurrentZAP(existingBuyerClusters)
    const existingBuyerClustersAfterRemoval = filter(
      associatedBuyerClustersOnCurrentZAP,
      cluster => cluster.id !== clusterId
    )
    if (
      existingBuyerClustersAfterRemoval.length === 0 &&
      buyingSessionProduct &&
      !this.isOpenDeleteProductConfirmationOnLastRMClusterDeleteDialog
    ) {
      this.setProductDetailsForRMLastClusterDelete(
        clusterId,
        zoneAssortmentProductId,
        buyingSessionProduct
      )
      this.openDeleteProductConfirmationOnLastRMClusterDeleteDialog()
      return null
    }
    if (existingBuyerClustersAfterRemoval.length === 0 && buyingSessionProduct) {
      const {
        product: {
          getHierarchyMetaFromBuyingSessionProduct,
          buildRefetchQueriesForAttributeHierarchy
        },
        nav: {
          params: { assortmentType }
        }
      } = stores
      let object = getHierarchyMetaFromBuyingSessionProduct(buyingSessionProduct)

      if (object) {
        refetchQueries = buildRefetchQueriesForAttributeHierarchy(
          [object],
          assortmentType
        )
      }
    }
    try {
      this.rmZoneAssortmentProductDeleteInProgress = true
      await apolloClient
        .mutate({
          mutation: REMOVE_RM_CLUSTER_FROM_PRODUCT,
          variables: {
            clusterId: clusterId,
            productId: zoneAssortmentProductId
          },
          refetchQueries
        })
        .then(() => {
          // Refetch counts only in case products are being refetched
          if (refetchQueries.length) {
            stores.product.executeAssortmentRefetchQueries()
          }
        })
    } finally {
      Object.assign(this, {
        rmZoneAssortmentProductDeleteInProgress: false
      })
      this.closeDeleteProductConfirmationOnLastRMClusterDeleteDialog()
    }
  }

  async getAssociatedBuyerClustersOnCurrentZAP(existingBuyerClusters) {
    const {
      nav: {
        params: { assortment: assortmentId }
      }
    } = stores

    const associatedClustersData = await apolloClient.query({
      query: GET_RM_CLUSTERS,
      variables: {
        zoneAssortmentId: assortmentId
      },
      fetchPolicy: 'cache-first'
    })

    return associatedClustersData &&
      associatedClustersData.data &&
      associatedClustersData.data.clusters
      ? intersectionBy(
          associatedClustersData.data.clusters,
          existingBuyerClusters,
          (cluster: any) => cluster.id
        )
      : []
  }

  /**
   * isClusterAssortment
   * Returns true if  its a cluster recommendation
   * or cluster assortment
   */
  isClusterView = (): boolean => {
    const {
      sidebar: { isRegionalMerchandiser },
      nav: {
        params: { assortmentType, clusterId }
      }
    } = stores

    return !!(assortmentType === strings.cluster || (isRegionalMerchandiser && clusterId))
  }

  @action showProductDetail = (e, product) => {
    const {
      history,
      location,
      params: { detail }
    } = nav
    let newUrl = location.pathname
    if (detail !== product.id) {
      newUrl = location.pathname.replace(
        `/details/${detail}`,
        `/details/${product.buyingSessionProductId}`
      )
    }
    if (!detail) {
      newUrl = `${location.pathname}/details/${product.buyingSessionProductId}`
    }
    history.push({ pathname: newUrl, search: history.location.search })
  }
}

export type AssortmentType = 'zone' | 'store' | 'global' | 'cluster'

export const assortment = new AssortmentStore()
